/**
 * Document generation — PDF (client-side via jsPDF) and Word (backend).
 * Both accept a structured DocSpec so Claude can describe the output once
 * and we render to the right format.
 */
import { jsPDF } from 'jspdf';
// jspdf-autotable extends jsPDF when imported for its side-effect
import 'jspdf-autotable';
import { getBootstrapToken } from './sso';

export type DocSection =
  | { type: 'heading'; text: string; level?: 1 | 2 | 3 }
  | { type: 'paragraph'; text: string; italic?: boolean; bold?: boolean }
  | { type: 'bullets'; items: string[] }
  | { type: 'numbered'; items: string[] }
  | { type: 'table'; headers: string[]; rows: (string | number | null)[][]; caption?: string }
  | { type: 'spacer' }
  | { type: 'disclaimer'; text: string };

export interface DocSpec {
  title: string;
  subtitle?: string;
  metadata?: { label: string; value: string }[];
  sections: DocSection[];
  footer?: string;
}

const NAVY = '#1F3864';
const SECONDARY = '#2E5597';
const TINT = '#D9E2F3';
const GREY = '#595959';
const AMBER_FILL = '#FFF4E5';
const AMBER_BORDER = '#F2A93B';

export function renderPdf(spec: DocSpec): Blob {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 48;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Title bar
  doc.setFillColor(NAVY);
  doc.rect(margin, y, contentWidth, 36, 'F');
  doc.setTextColor('#FFFFFF');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(spec.title, margin + 12, y + 22);
  y += 44;

  if (spec.subtitle) {
    doc.setTextColor(NAVY);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(spec.subtitle, margin, y);
    y += 16;
  }

  // Metadata
  if (spec.metadata?.length) {
    doc.setFontSize(9);
    doc.setTextColor(GREY);
    for (const m of spec.metadata) {
      doc.setFont('helvetica', 'bold');
      doc.text(m.label + ':', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(m.value, margin + 90, y);
      y += 12;
    }
    y += 8;
  }

  doc.setTextColor('#000000');
  doc.setFontSize(11);

  for (const sec of spec.sections) {
    if (y > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      y = margin;
    }
    if (sec.type === 'heading') {
      const sizes: Record<number, number> = { 1: 14, 2: 12, 3: 11 };
      const lvl = sec.level ?? 2;
      doc.setFontSize(sizes[lvl]);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(SECONDARY);
      y += 6;
      doc.text(sec.text, margin, y);
      y += sizes[lvl] + 4;
      doc.setTextColor('#000000');
      doc.setFontSize(11);
    } else if (sec.type === 'paragraph') {
      doc.setFont('helvetica', sec.bold ? 'bold' : sec.italic ? 'italic' : 'normal');
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(sec.text, contentWidth);
      doc.text(lines, margin, y);
      y += lines.length * 14 + 4;
      doc.setFont('helvetica', 'normal');
    } else if (sec.type === 'bullets') {
      doc.setFontSize(11);
      for (const item of sec.items) {
        const lines = doc.splitTextToSize('•  ' + item, contentWidth - 14);
        doc.text(lines, margin + 14, y);
        y += lines.length * 14;
      }
      y += 6;
    } else if (sec.type === 'numbered') {
      doc.setFontSize(11);
      sec.items.forEach((item, i) => {
        const lines = doc.splitTextToSize(`${i + 1}.  ${item}`, contentWidth - 18);
        doc.text(lines, margin + 18, y);
        y += lines.length * 14;
      });
      y += 6;
    } else if (sec.type === 'table') {
      const body = sec.rows.map(r => r.map(c => c == null ? '' : String(c)));
      // @ts-ignore - jspdf-autotable extends jsPDF
      doc.autoTable({
        head: [sec.headers],
        body,
        startY: y,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [31, 56, 100], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
      });
      // @ts-ignore
      y = (doc as any).lastAutoTable.finalY + 8;
      if (sec.caption) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(GREY);
        doc.text(sec.caption, margin, y);
        y += 12;
        doc.setTextColor('#000000');
        doc.setFont('helvetica', 'normal');
      }
    } else if (sec.type === 'spacer') {
      y += 12;
    } else if (sec.type === 'disclaimer') {
      doc.setFillColor(AMBER_FILL);
      doc.setDrawColor(AMBER_BORDER);
      const lines = doc.splitTextToSize(sec.text, contentWidth - 16);
      const h = lines.length * 12 + 16;
      doc.rect(margin, y, contentWidth, h, 'FD');
      doc.setTextColor(GREY);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.text(lines, margin + 8, y + 14);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#000000');
      doc.setFontSize(11);
      y += h + 8;
    }
  }

  if (spec.footer) {
    const pageCount = (doc as any).getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(GREY);
      const footer = `${spec.footer} — page ${p} of ${pageCount}`;
      doc.text(footer, margin, doc.internal.pageSize.getHeight() - 24);
    }
  }

  return doc.output('blob');
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

// process.env.API_BASE_URL is substituted at build time by webpack's
// DefinePlugin. See chat.ts for the same pattern.
const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

export async function generateWordDocx(spec: DocSpec): Promise<Blob> {
  const token = await getBootstrapToken();
  const res = await fetch(`${API_BASE_URL}/api/documents/word`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(spec),
    credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Word generation failed (${res.status}): ${txt}`);
  }
  return await res.blob();
}

export async function generatePowerPoint(spec: DocSpec): Promise<Blob> {
  const token = await getBootstrapToken();
  const res = await fetch(`${API_BASE_URL}/api/documents/powerpoint`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(spec),
    credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`PowerPoint generation failed (${res.status}): ${txt}`);
  }
  return await res.blob();
}

// ─────────────────────────────────────────────────────────────────────
// 2026-05-28 — Document UPLOAD (the other direction). v0.5 ships PDF only;
// v1 will accept DOCX and image MIME types when the backend allowlist is
// expanded. Frontend caller pattern:
//   const meta = await uploadDocument(fileFromFilePicker);
//   // show meta.fileName + meta.sizeBytes as a chip
//   // include meta.documentId in attachedDocumentIds[] on next chat send
//   // on chip X click: await deleteAttachedDocument(meta.documentId)
// ─────────────────────────────────────────────────────────────────────

export interface UploadedDocument {
  documentId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  ttlMinutes: number;
}

const ACCEPT_MIMES_V05 = ['application/pdf'];

export function isAcceptableUploadType(file: File): boolean {
  return ACCEPT_MIMES_V05.includes(file.type);
}

/** Uploads a PDF to the backend documentStore. Returns the stored doc's metadata. */
export async function uploadDocument(file: File): Promise<UploadedDocument> {
  if (!isAcceptableUploadType(file)) {
    throw new Error(`Unsupported file type "${file.type}". v0.5 accepts PDF only.`);
  }
  const form = new FormData();
  form.append('file', file);
  const token = await getBootstrapToken();
  const res = await fetch(`${API_BASE_URL}/api/documents/upload`, {
    method: 'POST',
    body: form,
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) {
    let errorMsg = `Upload failed (HTTP ${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) errorMsg = body.error;
    } catch { /* keep status-only message */ }
    throw new Error(errorMsg);
  }
  return (await res.json()) as UploadedDocument;
}

/** Lists this tenant's active uploaded documents. */
export async function listAttachedDocuments(): Promise<UploadedDocument[]> {
  const token = await getBootstrapToken();
  const res = await fetch(`${API_BASE_URL}/api/documents/_/list`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`List documents failed (HTTP ${res.status})`);
  }
  const data = await res.json() as { documents?: UploadedDocument[] };
  return data.documents ?? [];
}

/** Removes an uploaded document from the backend store. Called when the user clicks X on a chip. */
export async function deleteAttachedDocument(documentId: string): Promise<void> {
  const token = await getBootstrapToken();
  const res = await fetch(`${API_BASE_URL}/api/documents/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Delete document failed (HTTP ${res.status})`);
  }
}
